import React, { useState, useEffect } from 'react';
import './App.css';

function App() {
  const [gasPrices, setGasPrices] = useState({
    normal: '0',
    fast: '0',
    instant: '0',
    usd: {
      normal: '0',
      fast: '0',
      instant: '0'
    },
    hypePrice: '0',
    lastUpdated: new Date()
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchGasPrices = async () => {
    try {
      const response = await fetch('https://hypergas.io/api/gas');
      if (!response.ok) throw new Error('Failed to fetch gas prices');
      
      const data = await response.json();
      setGasPrices(data);
      setError(null);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGasPrices();
    const interval = setInterval(fetchGasPrices, 5000);
    return () => clearInterval(interval);
  }, []);

  const formatPrice = (price) => {
    const num = parseFloat(price);
    return isNaN(num) ? '0.00' : num.toFixed(2);
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString();
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a1119 0%, #1a252f 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: '20px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          color: '#e2e8f0',
          fontSize: 'clamp(16px, 4vw, 18px)',
          fontWeight: '500',
          textAlign: 'center'
        }}>
          <div style={{
            width: '20px',
            height: '20px',
            border: '2px solid #97fce4',
            borderTop: '2px solid transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          Loading gas prices...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a1119 0%, #1a252f 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: '20px'
      }}>
        <div style={{
          backgroundColor: 'rgba(30, 41, 59, 0.8)',
          border: '1px solid #ef4444',
          borderRadius: '12px',
          padding: 'clamp(16px, 4vw, 24px)',
          color: '#ef4444',
          fontSize: 'clamp(16px, 4vw, 18px)',
          fontWeight: '500',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          textAlign: 'center',
          maxWidth: '90vw'
        }}>
          Error: {error}
        </div>
      </div>
    );
  }

  const cardStyle = {
    backgroundColor: 'rgba(30, 41, 59, 0.6)',
    backdropFilter: 'blur(12px)',
    border: '1px solid rgba(151, 252, 228, 0.15)',
    borderRadius: '16px',
    padding: 'clamp(20px, 5vw, 32px)',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    position: 'relative',
    overflow: 'hidden'
  };

  const glowEffect = {
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    height: '1px',
    background: 'linear-gradient(90deg, transparent, rgba(151, 252, 228, 0.5), transparent)'
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a1119 0%, #1a252f 100%)',
      padding: 'clamp(20px, 5vw, 40px) clamp(16px, 4vw, 20px)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          .gas-card:hover {
            transform: translateY(-4px);
            box-shadow: 0 25px 50px -12px rgba(151, 252, 228, 0.2);
            border-color: rgba(151, 252, 228, 0.3);
          }
          @media (max-width: 768px) {
            .gas-card:hover {
              transform: none;
            }
            .gas-card:active {
              transform: scale(0.98);
              transition: transform 0.1s ease;
            }
          }
          @media (max-width: 480px) {
            .header-flex {
              flex-direction: column !important;
              gap: 16px !important;
            }
            .hype-price-mobile {
              order: -1;
            }
          }
        `}
      </style>
      
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 'clamp(32px, 8vw, 48px)' }}>
          <div className="header-flex" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'clamp(16px, 4vw, 20px)',
            marginBottom: 'clamp(16px, 4vw, 20px)',
            flexWrap: 'wrap'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'clamp(8px, 2vw, 12px)'
            }}>
              <div style={{
                width: 'clamp(8px, 2vw, 12px)',
                height: 'clamp(8px, 2vw, 12px)',
                backgroundColor: '#97fce4',
                borderRadius: '50%',
                animation: 'pulse 2s infinite'
              }}></div>
              <h1 style={{
                fontSize: 'clamp(28px, 8vw, 48px)',
                fontWeight: '700',
                background: 'linear-gradient(135deg, #97fce4 0%, #4ade80 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                margin: '0',
                letterSpacing: '-0.02em',
                lineHeight: '1.1'
              }}>
                HyperEVM Gas
              </h1>
            </div>
            
            {/* HYPE Price Display */}
            {parseFloat(gasPrices.hypePrice) > 0 && (
              <div className="hype-price-mobile" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: 'rgba(151, 252, 228, 0.1)',
                padding: 'clamp(6px, 2vw, 8px) clamp(12px, 3vw, 16px)',
                borderRadius: '20px',
                border: '1px solid rgba(151, 252, 228, 0.2)',
                backdropFilter: 'blur(12px)'
              }}>
                <span style={{ fontSize: 'clamp(14px, 3vw, 16px)' }}>💎</span>
                <div>
                  <div style={{
                    color: '#97fce4',
                    fontSize: 'clamp(9px, 2vw, 11px)',
                    fontWeight: '500',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    lineHeight: '1'
                  }}>
                    HYPE Price
                  </div>
                  <div style={{
                    color: '#ffffff',
                    fontSize: 'clamp(16px, 4vw, 18px)',
                    fontWeight: '700',
                    fontFeatureSettings: '"tnum"',
                    lineHeight: '1'
                  }}>
                    ${parseFloat(gasPrices.hypePrice).toFixed(2)}
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <p style={{
            color: '#94a3b8',
            fontSize: 'clamp(14px, 4vw, 18px)',
            fontWeight: '400',
            margin: '0 0 clamp(8px, 2vw, 8px) 0',
            lineHeight: '1.4',
            padding: '0 16px'
          }}>
            Real-Time Gas Fee Estimates  •  Updates Every 5 Seconds
          </p>
          
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: 'rgba(30, 41, 59, 0.6)',
            padding: 'clamp(6px, 2vw, 8px) clamp(12px, 3vw, 16px)',
            borderRadius: '24px',
            border: '1px solid rgba(151, 252, 228, 0.1)'
          }}>
            <div style={{
              width: '6px',
              height: '6px',
              backgroundColor: '#97fce4',
              borderRadius: '50%'
            }}></div>
            <span style={{
              color: '#94a3b8',
              fontSize: 'clamp(12px, 3vw, 14px)',
              fontWeight: '500'
            }}>
              Last updated: {formatTime(gasPrices.lastUpdated)}
            </span>
          </div>
        </div>

        {/* Gas Price Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
          gap: 'clamp(16px, 4vw, 24px)',
          marginBottom: 'clamp(32px, 6vw, 40px)'
        }}>
          {/* Normal */}
          <div className="gas-card" style={{
            ...cardStyle,
            borderTopColor: 'rgba(151, 252, 228, 0.4)'
          }}>
            <div style={glowEffect}></div>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: 'clamp(40px, 10vw, 48px)',
                height: 'clamp(40px, 10vw, 48px)',
                backgroundColor: 'rgba(151, 252, 228, 0.15)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto clamp(16px, 4vw, 20px)',
                border: '1px solid rgba(151, 252, 228, 0.2)',
                fontSize: 'clamp(20px, 5vw, 24px)'
              }}>
                🐢
              </div>
              
              <h3 style={{
                fontSize: 'clamp(14px, 3vw, 16px)',
                fontWeight: '600',
                color: '#97fce4',
                margin: '0 0 clamp(12px, 3vw, 16px) 0',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Standard
              </h3>
              
              <div style={{
                fontSize: 'clamp(32px, 8vw, 42px)',
                fontWeight: '700',
                color: '#ffffff',
                margin: '0 0 4px 0',
                fontFeatureSettings: '"tnum"',
                lineHeight: '1'
              }}>
                {formatPrice(gasPrices.normal)}
              </div>
              
              <div style={{
                color: '#94a3b8',
                fontSize: 'clamp(14px, 3vw, 16px)',
                fontWeight: '500',
                marginBottom: '8px'
              }}>
                gwei
              </div>
              
              <div style={{
                color: '#97fce4',
                fontSize: 'clamp(12px, 3vw, 14px)',
                fontWeight: '600',
                marginBottom: '12px'
              }}>
                ~${gasPrices.usd?.normal || '0.000000'} USD
              </div>
              
              <div style={{
                color: '#64748b',
                fontSize: 'clamp(12px, 3vw, 14px)',
                backgroundColor: 'rgba(151, 252, 228, 0.1)',
                padding: '6px 12px',
                borderRadius: '6px',
                display: 'inline-block'
              }}>
                Base network rate
              </div>
            </div>
          </div>

          {/* Fast */}
          <div className="gas-card" style={{
            ...cardStyle,
            borderTopColor: 'rgba(251, 191, 36, 0.4)'
          }}>
            <div style={{
              ...glowEffect,
              background: 'linear-gradient(90deg, transparent, rgba(251, 191, 36, 0.5), transparent)'
            }}></div>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: 'clamp(40px, 10vw, 48px)',
                height: 'clamp(40px, 10vw, 48px)',
                backgroundColor: 'rgba(251, 191, 36, 0.15)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto clamp(16px, 4vw, 20px)',
                border: '1px solid rgba(251, 191, 36, 0.2)',
                fontSize: 'clamp(20px, 5vw, 24px)'
              }}>
                🏃
              </div>
              
              <h3 style={{
                fontSize: 'clamp(14px, 3vw, 16px)',
                fontWeight: '600',
                color: '#fbbf24',
                margin: '0 0 clamp(12px, 3vw, 16px) 0',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Fast
              </h3>
              
              <div style={{
                fontSize: 'clamp(32px, 8vw, 42px)',
                fontWeight: '700',
                color: '#ffffff',
                margin: '0 0 4px 0',
                fontFeatureSettings: '"tnum"',
                lineHeight: '1'
              }}>
                {formatPrice(gasPrices.fast)}
              </div>
              
              <div style={{
                color: '#94a3b8',
                fontSize: 'clamp(14px, 3vw, 16px)',
                fontWeight: '500',
                marginBottom: '8px'
              }}>
                gwei
              </div>
              
              <div style={{
                color: '#fbbf24',
                fontSize: 'clamp(12px, 3vw, 14px)',
                fontWeight: '600',
                marginBottom: '12px'
              }}>
                ~${gasPrices.usd?.fast || '0.000000'} USD
              </div>
              
              <div style={{
                color: '#64748b',
                fontSize: 'clamp(12px, 3vw, 14px)',
                backgroundColor: 'rgba(251, 191, 36, 0.1)',
                padding: '6px 12px',
                borderRadius: '6px',
                display: 'inline-block'
              }}>
                +15% priority
              </div>
            </div>
          </div>

          {/* Instant */}
          <div className="gas-card" style={{
            ...cardStyle,
            borderTopColor: 'rgba(239, 68, 68, 0.4)'
          }}>
            <div style={{
              ...glowEffect,
              background: 'linear-gradient(90deg, transparent, rgba(239, 68, 68, 0.5), transparent)'
            }}></div>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: 'clamp(40px, 10vw, 48px)',
                height: 'clamp(40px, 10vw, 48px)',
                backgroundColor: 'rgba(239, 68, 68, 0.15)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto clamp(16px, 4vw, 20px)',
                border: '1px solid rgba(239, 68, 68, 0.2)',
                fontSize: 'clamp(20px, 5vw, 24px)'
              }}>
                ⚡
              </div>
              
              <h3 style={{
                fontSize: 'clamp(14px, 3vw, 16px)',
                fontWeight: '600',
                color: '#ef4444',
                margin: '0 0 clamp(12px, 3vw, 16px) 0',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Instant
              </h3>
              
              <div style={{
                fontSize: 'clamp(32px, 8vw, 42px)',
                fontWeight: '700',
                color: '#ffffff',
                margin: '0 0 4px 0',
                fontFeatureSettings: '"tnum"',
                lineHeight: '1'
              }}>
                {formatPrice(gasPrices.instant)}
              </div>
              
              <div style={{
                color: '#94a3b8',
                fontSize: 'clamp(14px, 3vw, 16px)',
                fontWeight: '500',
                marginBottom: '8px'
              }}>
                gwei
              </div>
              
              <div style={{
                color: '#ef4444',
                fontSize: 'clamp(12px, 3vw, 14px)',
                fontWeight: '600',
                marginBottom: '12px'
              }}>
                ~${gasPrices.usd?.instant || '0.000000'} USD
              </div>
              
              <div style={{
                color: '#64748b',
                fontSize: 'clamp(12px, 3vw, 14px)',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                padding: '6px 12px',
                borderRadius: '6px',
                display: 'inline-block'
              }}>
                +40% priority
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          textAlign: 'center',
          padding: 'clamp(16px, 4vw, 24px)',
          backgroundColor: 'rgba(30, 41, 59, 0.6)',
          borderRadius: '12px',
          border: '1px solid rgba(151, 252, 228, 0.1)'
        }}>
          <div style={{
            color: '#94a3b8',
            fontSize: 'clamp(12px, 3vw, 14px)',
            fontWeight: '500',
            lineHeight: '1.5'
          }}>
            Powered by HyperEVM • Built with React & Ethers.js • By{' '}
            <a 
              href="https://x.com/rusty_hl" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{
                color: '#97fce4',
                textDecoration: 'none',
                fontWeight: '600'
              }}
            >
              @rusty_hl
            </a>
            {' '}on X
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;